import getConfig from 'next/config'

import type { Context as CartContext } from '@grandvisionhq/carts-v2'
import type { CheckoutSettings, Consent, Routes } from '@grandvisionhq/checkout-v2'
import { IntlContextModel, Locales } from '@grandvisionhq/state'
import { Util as StoreUtils } from '@grandvisionhq/stores-v2'

import { CheckoutAddressForm } from '~/components/forms/Address'
import ContactDetailsForm from '~/components/forms/ContactDetails'
import AddressSummary from '~/components/summaries/Address'
import ContactDetailsSummary from '~/components/summaries/ContactDetails'

interface SettingsInput {
  cart: CartContext.CartContextModel['cart']
  locale: Locales
  getLabel: IntlContextModel['getLabel']
}

const { publicRuntimeConfig } = getConfig()
const { orderTimeOut } = publicRuntimeConfig.checkout

// Configuration for forms required to fill in Checkout shipping step
export const stepShippingFormsConfig = {
  contactDetails: ContactDetailsForm,
  billingAddress: CheckoutAddressForm,
  deliveryAddress: CheckoutAddressForm,
}

// Configuration for summaries on Checkout Overview step
export const stepOverviewSummariesConfig = {
  contactDetails: ContactDetailsSummary,
  billingAddress: AddressSummary,
  deliveryAddress: AddressSummary,
}

export const getCheckoutConfig = ({ cart, locale, getLabel }: SettingsInput): CheckoutSettings => {
  let shippingDetails: CheckoutSettings['shippingDetails'] | undefined

  const hasSubscription = cart?.contains.subscription
  const consents: Consent[] = hasSubscription
    ? [
        {
          title: getLabel('checkout.subscriptionConsent'),
          value: 'subscriptionConsent',
        },
      ]
    : []

  const paymentMethodLogos = {
    bancontactMobile: {
      url: '//cdn.grandvision.io/binaries/content/gallery/be-pearle/logos/payment-logos/bancontact.svg',
    },
  }

  if (cart && (cart.billingAddress || cart.shippingAddress || cart.customerDetails)) {
    shippingDetails = {
      ...(cart.billingAddress ? { billingAddress: cart.billingAddress } : {}),
      ...(cart.shippingAddress ? { deliveryAddress: cart.shippingAddress } : {}),
      ...(cart.customerDetails ? { customer: cart.customerDetails } : {}),
    }
  }

  const basePath = '/checkout'
  const routes: Routes = {
    login: {
      label: locale === Locales['nl-BE'] ? 'Je account' : 'Votre compte',
      path: `${basePath}/login`,
    },
    shipping: {
      label: getLabel('checkout.shipment.title'),
      path: `${basePath}/shipping`,
    },
    overview: {
      label: getLabel('checkout.summary.title'),
      path: `${basePath}/overview`,
    },
    payment: {
      label: getLabel('checkout.payment.title'),
      path: `${basePath}/payment`,
    },
    completed: {
      label: getLabel('checkout.completed.title'),
      path: `${basePath}/thankyou`,
    },
    callback: {
      label: 'Callback',
      path: `${basePath}/callback`,
    },
  }

  return {
    account: {
      implicitAccountCreation: false,
      createAccountRequired: false,
    },
    consent: {
      items: consents,
      implicitTermsAndConditions: true,
    },
    shippingDetails: shippingDetails ? shippingDetails : {},
    storeFinder: {
      showUseMyLocation: true,
      googleMapsZoom: 6,
      searchWhitelist: ['storeName', 'postalCode', 'streetName', 'town'] as StoreUtils.SearchKey[],
    },
    ...(orderTimeOut ? { orderCreationTimeOutLimit: orderTimeOut } : {}),
    features: {
      isCompanyEnabled: false,
      isServiceStoreEnabled: true,
    },
    login: {
      disableSignup: true,
      returnPath: routes.shipping.path,
    },
    routes,
    paymentMethodLogos,
    logoVariant: 'alternative',
  }
}
